import { Injectable, NgZone } from "@angular/core";
import { BehaviorSubject, Observable, Subject, Subscription } from "rxjs";
import { Helper } from "src/app/core/helpers/helper";
import { SocketService } from "./socket.service";
import { BookingService } from "./booking.service";

/**
 * Spec #1 — Caller Information.
 *
 * Singleton state + socket bridge for the incoming-call popup. Wired once in
 * the layout shell so the popup fires regardless of which dispatcher page the
 * dispatcher is currently looking at.
 *
 * Lifecycle:
 *   - `init()` is called by the layout after the dispatcher's profile is
 *     known. It joins the dispatcher's `countryId` socket room and starts
 *     listening for the server-emitted `INCOMING_CALL` event (see
 *     server/.../incomingCall.controller.ts).
 *   - When a call arrives, we run the search ladder:
 *        (1) lookup customer  → if found, show "customer" state
 *        (2) lookup driver    → if found, show "driver" state
 *        (3) neither          → show "new" state (open Quick Booking as new)
 *   - The dispatcher can manually escalate from "customer" → also-look-up
 *     driver (and vice versa) via `lookupAsDriver()` / `lookupAsCustomer()`.
 */
export type IncomingCallState =
  | "idle"
  | "searching-customer"
  | "customer-found"
  | "searching-driver"
  | "driver-found"
  | "not-found";

export interface IncomingCallEvent {
  callId: string;
  fromNumber: string;
  fromCountryPhoneCode: string;
  startedAt: number;
  /** Optional caller details forwarded by Dialpad's `contact` object. */
  contactName?: string;
  contactEmail?: string;
}

/**
 * Server-side CallEventState mirror — kept here so the FE doesn't depend on
 * the server enum. Values must stay in sync with
 * server/.../incomingCall.model.ts.
 */
export enum IncomingCallEventState {
  RINGING = 1,
  ANSWERED = 2,
  HANGUP = 3,
  MISSED = 4,
  VOICEMAIL = 5
}

export interface IncomingCallSnapshot {
  state: IncomingCallState;
  event: IncomingCallEvent | null;
  customer: any | null;
  driver: any | null;
  /** Past trips from `GET /api/dispatcher/search_customer`. */
  recentTrips?: any[];
  /** In-progress trip from the same customer search, when the caller has one. */
  activeBooking?: any | null;
}

@Injectable({ providedIn: "root" })
export class IncomingCallService {
  /** Latest snapshot — bound by the modal template. */
  private readonly snapshot$ = new BehaviorSubject<IncomingCallSnapshot>({
    state: "idle",
    event: null,
    customer: null,
    driver: null
  });

  /**
   * Incoming calls that arrived while the dispatcher was already on a call.
   * The dispatcher sees a small floating widget listing them; clicking one
   * promotes it to the active call (replacing whatever's active).
   */
  private readonly queue$ = new BehaviorSubject<IncomingCallEvent[]>([]);

  /** Edge-trigger signal — the modal opens on each new value. */
  private readonly openModal$ = new Subject<void>();

  /**
   * Fires when the caller terminates a call (HANGUP / MISSED / VOICEMAIL).
   * Emits the callId so subscribers (Quick Booking, etc.) can correlate it
   * with whichever call they were tracking and update their UI.
   */
  private readonly callEnded$ = new Subject<string>();

  private socketSubscription?: Subscription;
  private joinedRoomId: string | null = null;
  private initialized = false;
  /** Bound reference so we can detach on destroy. */
  private connectHandler?: () => void;
  /**
   * Latest call timestamp we've handled — used as the `since` filter on every
   * `/incoming_call/recent` catch-up. Persisted across reloads in localStorage
   * so a hard refresh doesn't lose the cursor and drop recently-arrived calls
   * the dispatcher hadn't yet acknowledged.
   *
   * Default on first ever visit / cleared storage: look back 5 minutes so
   * dispatchers see calls that arrived just before their tab finished loading,
   * but not a flood of old history.
   */
  private static readonly LAST_SEEN_KEY = "rhero.incomingCall.lastSeenAt";
  private static readonly INITIAL_LOOKBACK_MS = 5 * 60 * 1000;
  private lastSeenAt: number = this.loadLastSeenAt();

  constructor(
    private helper: Helper,
    private socketService: SocketService,
    private bookingService: BookingService,
    private zone: NgZone
  ) {}

  /** Bound once from the layout shell — safe to call multiple times. */
  init(): void {
    if (this.initialized) return;
    const countryId = this.helper.dispatcherDetails?.entity?.countryId;
    if (!countryId || !this.socketService.socket) {
      console.warn("[IncomingCall] init skipped — missing", {
        hasCountryId: !!countryId,
        hasSocket: !!this.socketService.socket
      });
      return;
    }
    console.info("[IncomingCall] init: joining country room", String(countryId));

    this.initialized = true;
    this.joinedRoomId = String(countryId);

    // Socket.io reconnects automatically, but on each reconnect the server
    // sees a *new* socket ID — so it's no longer in any room and won't deliver
    // INCOMING_CALL until we re-join. Bind to the `connect` event so the room
    // is re-joined every time the connection comes up.
    this.connectHandler = () => this.joinRoomAndCatchUp();
    this.socketService.socket.on("connect", this.connectHandler);

    // First-time join if already connected (the connect event won't fire again).
    if (this.socketService.socket.connected) {
      this.joinRoomAndCatchUp();
    }

    // socket.io preserves `on(eventName, ...)` handlers across reconnects, so
    // subscribing once here is enough — only room membership needs the dance
    // above.
    this.socketSubscription = this.socketService.listener("INCOMING_CALL").subscribe((res: any) => {
      // Socket callbacks run outside Angular zone in some configs — wrap so
      // change detection fires on the modal binding.
      this.zone.run(() => {
        // SECURITY: do not log the caller payload (phone number / name) to the browser console.
        if (res?.data?.startedAt) {
          const ts = new Date(res.data.startedAt).getTime();
          if (!Number.isNaN(ts)) this.bumpLastSeen(ts);
        }
        this.handleIncomingCall(res?.data);
      });
    });
  }

  destroy(): void {
    this.socketSubscription?.unsubscribe();
    this.socketSubscription = undefined;
    if (this.connectHandler && this.socketService.socket) {
      this.socketService.socket.off("connect", this.connectHandler);
      this.connectHandler = undefined;
    }
    if (this.joinedRoomId && this.socketService.socket) {
      this.socketService.emit(this.helper.SocketEvents.LEAVE_ROOM, { event: "LEAVE_ROOM", data: { roomId: this.joinedRoomId } });
    }
    this.joinedRoomId = null;
    this.initialized = false;
  }

  /**
   * (Re-)join the country room, then fetch any call events the server
   * persisted while we were disconnected. Events newer than `lastSeenAt` are
   * replayed through `handleIncomingCall` so the dispatcher never misses one.
   */
  private joinRoomAndCatchUp(): void {
    if (!this.joinedRoomId) return;
    this.socketService.sendAndListenEvent(
      this.helper.SocketEvents.JOIN_ROOM,
      { event: "JOIN_ROOM", data: { roomId: this.joinedRoomId } },
      () => {}
    );
    // Fire-and-forget the catch-up. We don't await it because joining the room
    // is the priority — the fetch is just belt-and-braces for missed events.
    this.catchUpMissedCalls();
  }

  private catchUpMissedCalls(): void {
    const since = this.lastSeenAt;
    this.bookingService.listMissedIncomingCalls(since).subscribe({
      next: (calls: any[]) => {
        // Replay newest-first calls oldest-first so the latest one ends up on top.
        const ordered = [...(calls || [])].sort((a, b) => {
          const ta = new Date(a.startedAt || a.createdAt || 0).getTime();
          const tb = new Date(b.startedAt || b.createdAt || 0).getTime();
          return ta - tb;
        });
        if (ordered.length > 0) {
          console.info(`[IncomingCall] catch-up: replaying ${ordered.length} missed event(s)`);
        }
        ordered.forEach((c) => {
          const ts = new Date(c.startedAt || c.createdAt || 0).getTime();
          if (Number.isFinite(ts)) this.bumpLastSeen(ts);
          this.zone.run(() =>
            this.handleIncomingCall({
              callId: c.callId,
              fromNumber: c.fromNumber,
              fromCountryPhoneCode: c.fromCountryPhoneCode || "",
              startedAt: c.startedAt
            })
          );
        });
      },
      error: () => {
        /* Swallow — catchup is best-effort. */
      }
    });
  }

  state$(): Observable<IncomingCallSnapshot> {
    return this.snapshot$.asObservable();
  }

  /** Stream of queued calls (those that arrived while another was active). */
  queue$$(): Observable<IncomingCallEvent[]> {
    return this.queue$.asObservable();
  }

  /** Edge-trigger so the modal opens on each new call. */
  onOpen$(): Observable<void> {
    return this.openModal$.asObservable();
  }

  /** Stream of callIds that just ended (caller hung up / voicemail). */
  onCallEnded$(): Observable<string> {
    return this.callEnded$.asObservable();
  }

  /** Dispatcher dismissed the popup — go back to idle. Does NOT clear the queue. */
  dismiss(): void {
    this.snapshot$.next({ state: "idle", event: null, customer: null, driver: null });
  }

  /**
   * Promote a queued call to the active call. Whatever's currently active is
   * dropped (the dispatcher chose to pivot). The queue is unchanged for other
   * calls; only this one is removed.
   */
  answerQueued(callId: string): void {
    const queued = this.queue$.value.find((c) => c.callId === callId);
    if (!queued) return;
    this.queue$.next(this.queue$.value.filter((c) => c.callId !== callId));
    // Run the same flow as a fresh socket event.
    this.zone.run(() => this.handleIncomingCall(queued));
  }

  /** Remove a queued call without answering it (dispatcher ignored / sent to voicemail). */
  dismissQueued(callId: string): void {
    this.queue$.next(this.queue$.value.filter((c) => c.callId !== callId));
  }

  /** Manual escalation: dispatcher wants to *also* check the driver collection. */
  lookupAsDriver(): void {
    const snap = this.snapshot$.value;
    if (!snap.event) return;
    this.snapshot$.next({ ...snap, state: "searching-driver" });
    this.bookingService
      .searchDriverByPhone({ phone: snap.event.fromNumber, countryPhoneCode: snap.event.fromCountryPhoneCode })
      .subscribe((driver: any) => {
        const next = this.snapshot$.value;
        if (driver) {
          this.snapshot$.next({ ...next, state: "driver-found", driver });
        } else {
          // No driver either — if we already had a customer keep showing them,
          // otherwise drop to the "new" state.
          this.snapshot$.next({
            ...next,
            state: next.customer ? "customer-found" : "not-found",
            driver: null
          });
        }
      });
  }

  /** Manual escalation back to customer view if the dispatcher pivoted to driver. */
  lookupAsCustomer(): void {
    const snap = this.snapshot$.value;
    if (!snap.event) return;
    this.runCustomerLookup(snap.event);
  }

  /** Allow the test harness / curl-based testing to fake an incoming call. */
  fakeIncomingCall(event: IncomingCallEvent): void {
    this.zone.run(() => this.handleIncomingCall(event));
  }

  // ─── lastSeenAt persistence ──────────────────────────────────────────

  /**
   * Load cursor from localStorage. On first ever visit / cleared storage,
   * default to `now - INITIAL_LOOKBACK_MS` so the catch-up picks up calls
   * that arrived in the last few minutes (the typical "I just opened the
   * tab and missed a ringing call" window).
   */
  private loadLastSeenAt(): number {
    try {
      const raw = localStorage.getItem(IncomingCallService.LAST_SEEN_KEY);
      const parsed = raw ? parseInt(raw, 10) : NaN;
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    } catch { /* localStorage blocked — fall through */ }
    return Date.now() - IncomingCallService.INITIAL_LOOKBACK_MS;
  }

  private bumpLastSeen(ts: number): void {
    if (!Number.isFinite(ts) || ts <= this.lastSeenAt) return;
    this.lastSeenAt = ts;
    try {
      localStorage.setItem(IncomingCallService.LAST_SEEN_KEY, String(ts));
    } catch { /* storage may be blocked / quota — silently ignore */ }
  }

  // ─────────────────────────────────────────────────────────────────────

  private handleIncomingCall(data: any): void {
    if (!data?.fromNumber) return;
    const event: IncomingCallEvent = {
      callId: String(data.callId || ""),
      fromNumber: String(data.fromNumber || ""),
      fromCountryPhoneCode: String(data.fromCountryPhoneCode || ""),
      startedAt: data.startedAt ? new Date(data.startedAt).getTime() : Date.now(),
      contactName: data.contactName || "",
      contactEmail: data.contactEmail || ""
    };

    // Branch on the lifecycle state. The server emits one socket event per
    // Dialpad state (ringing / connected / hangup / voicemail / …); the
    // frontend only cares about a few transitions.
    const state = Number(data.state || IncomingCallEventState.RINGING);

    // Caller terminated the call (or it was completed) — clear it from
    // wherever it lives so the dispatcher isn't staring at a stale popup.
    if (state === IncomingCallEventState.HANGUP ||
        state === IncomingCallEventState.MISSED ||
        state === IncomingCallEventState.VOICEMAIL) {
      this.removeCall(event.callId);
      return;
    }

    // Anything other than RINGING / ANSWERED at this point we don't act on.
    // (ANSWERED is informational — the dispatcher already saw the modal on
    // RINGING; we don't pop again.)
    if (state !== IncomingCallEventState.RINGING) return;

    // De-dupe: if the same callId is already active or queued, ignore the
    // repeat. Webhook retries + reconnect catch-up can both deliver twice.
    if (this.snapshot$.value.event?.callId === event.callId) return;
    if (this.queue$.value.some((c) => c.callId === event.callId)) return;

    // If a call is already being handled (anything other than 'idle'), hold
    // the new one on the queue and let the dispatcher get to it when ready.
    if (this.snapshot$.value.state !== "idle") {
      this.queue$.next([...this.queue$.value, event]);
      return;
    }

    this.snapshot$.next({ state: "searching-customer", event, customer: null, driver: null });
    this.openModal$.next();
    this.runCustomerLookup(event);
  }

  /**
   * Remove a callId from wherever it lives — active snapshot or the queue.
   * Used when the caller terminates the call (HANGUP / MISSED / VOICEMAIL)
   * before the dispatcher has finished with it.
   */
  private removeCall(callId: string): void {
    if (!callId) return;
    // Active call — drop it back to idle so the next queued one can be picked up.
    if (this.snapshot$.value.event?.callId === callId) {
      this.snapshot$.next({ state: "idle", event: null, customer: null, driver: null });
    }
    // Queued copy — quietly remove.
    const queued = this.queue$.value.find((c) => c.callId === callId);
    if (queued) {
      this.queue$.next(this.queue$.value.filter((c) => c.callId !== callId));
    }
    // Notify subscribers that follow specific callIds (e.g. Quick Booking
    // shows the caller's pane and wants to flip its banner to "Call ended").
    this.callEnded$.next(callId);
  }

  private runCustomerLookup(event: IncomingCallEvent): void {
    this.bookingService
      .getCustomerDetails({
        searchText: event.fromNumber,
        countryPhoneCode: event.fromCountryPhoneCode,
        isShowErrorToast: false
      })
      .subscribe((response: any) => {
        if (response && response.customer) {
          this.snapshot$.next({
            state: "customer-found",
            event,
            customer: response.customer,
            driver: this.snapshot$.value.driver,
            recentTrips: response.recentTrips || response.trips || [],
            activeBooking: response.activeBooking || null
          });
        } else {
          // Customer miss — automatically try the driver collection.
          this.snapshot$.next({ state: "searching-driver", event, customer: null, driver: null });
          this.bookingService
            .searchDriverByPhone({ phone: event.fromNumber, countryPhoneCode: event.fromCountryPhoneCode })
            .subscribe((driver: any) => {
              if (driver) {
                this.snapshot$.next({ state: "driver-found", event, customer: null, driver });
              } else {
                this.snapshot$.next({ state: "not-found", event, customer: null, driver: null });
              }
            });
        }
      });
  }
}
